import { useState } from 'react';

import * as LucideIcons from 'lucide-react';
import { Database, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

interface ConnectorIconProps {
  connector: {
    icon: string;
    iconUrl?: string;
    name: string;
  };
  className?: string;
}

// Type for accessing Lucide icons by name
type LucideIconsType = Record<string, LucideIcon>;

/**
 * ConnectorIcon - Renders connector icon with fallback support
 *
 * Priority:
 * 1. iconUrl (custom logo from CDN/storage)
 * 2. icon (Lucide icon name)
 * 3. Database (fallback icon)
 */
export function ConnectorIcon({ connector, className }: ConnectorIconProps) {
  const [imageError, setImageError] = useState(false);
  const { icon, iconUrl, name } = connector;

  // If iconUrl is provided and hasn't errored, render as image
  // Handle dark mode variants: swap -dark.svg to -light.svg in dark mode
  if (iconUrl && !imageError) {
    let resolvedUrl = iconUrl;
    if (iconUrl.includes('-dark.svg')) {
      const isDark = document.documentElement.classList.contains('dark');
      if (isDark) {
        resolvedUrl = iconUrl.replace('-dark.svg', '-light.svg');
      }
    }
    return (
      <img
        src={resolvedUrl}
        alt={`${name} icon`}
        className={cn('object-contain', className)}
        onError={() => setImageError(true)}
      />
    );
  }

  // Try to get Lucide icon by name
  const IconComponent = (LucideIcons as unknown as LucideIconsType)[icon];

  if (IconComponent) {
    return <IconComponent className={className} />;
  }

  // Fallback to Database icon
  return <Database className={className} />;
}

export default ConnectorIcon;
